import { useState, useRef, ChangeEvent } from 'react';
import classNames from 'classnames/bind';
import styles from './StudentBulkAddRequest.module.css';
import {
  STUDENT_BULK_ADD_REQUEST,
  STUDENT_BULK_ADD_REQUEST_DESC,
  REQUEST_TYPE,
  FORM_DOWNLOAD,
  REQUEST_CONTENT_WRITE,
  STUDENT_BULK_ADD_ACTION,
  GROUP_BULK_ADD_ACTION,
  ETC,
  STUDENT_INFO_EXCEL_FORM,
  PRIVACY_CONSENT_FORM,
  EXCEL_UPLOAD_PLACEHOLDER,
  CONSENT_UPLOAD_PLACEHOLDER,
  GROUP_INFO_EXCEL_FORM,
  GROUP_BULK_ADD_NOTICE,
  GROUP_UPLOAD_PLACEHOLDER,
  FILE_UPLOAD_PLACEHOLDER,
  UPLOAD_NOTICE,
  REQUEST_ACTION,
  ENTER_TITLE,
  ENTER_CONTENT,
  BROWSE,
  CHARACTER_COUNT,
} from '../../../constants';
import { downloadByUrl } from '../../../utils/util';
import parse from 'html-react-parser';
import { useAppSelector } from '../../../hooks/useRedux';
import { selectStudentCount } from '../../../features/classroomSlice';

export default function StudentBulkAddRequest() {
  const cx = classNames.bind(styles);

  const [requestType, setRequestType] = useState('student');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [consentFile, setConsentFile] = useState<File | null>(null);

  const excelInputRef = useRef<HTMLInputElement>(null);
  const consentInputRef = useRef<HTMLInputElement>(null);

  const studentCount = useAppSelector(selectStudentCount);
  const isNoStudent = requestType === 'group' && studentCount === 0;

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= 25) {
      setTitle(e.target.value);
    }
  };

  const handleContentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= 1000) {
      setContent(e.target.value);
    }
  };

  const handleExcelFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setExcelFile(e.target.files[0]);
    }
  };

  const handleConsentFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setConsentFile(e.target.files[0]);
    }
  };

  const handleRequestClick = () => {
    // TODO: 요청 로직 구현
    console.log('Request submitted:', {
      requestType,
      title,
      content,
      excelFile,
      consentFile,
    });
  };

  const handleDownload = (
    e: React.MouseEvent,
    type: 'excel' | 'consent' | 'group'
  ) => {
    e.preventDefault();
    // TODO: URL 수정
    let url = '';
    if (type === 'group' && isNoStudent) {
      return;
    }

    if (type === 'excel') {
      url = '/samples/student_list.xlsx';
    } else if (type === 'consent') {
      url = '/samples/consent.docx';
    } else if (type === 'group') {
      url = '/samples/group_list.xlsx';
    }

    if (url) {
      downloadByUrl(url);
    }
  };

  interface FileUploadFieldProps {
    file: File | null;
    placeholder: string;
    inputRef: React.RefObject<HTMLInputElement>;
    accept: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }

  const FileUploadField = ({
    file,
    placeholder,
    inputRef,
    accept,
    onChange,
  }: FileUploadFieldProps) => (
    <div className={cx('file_upload_field')}>
      <div
        className={cx('file_upload_text', {
          placeholder: !file,
        })}
        title={file?.name || ''}
      >
        {file ? file.name : placeholder}
      </div>
      <button
        type="button"
        className={cx('file_upload_button')}
        onClick={() => inputRef.current?.click()}
      >
        {BROWSE}
      </button>
      <input
        type="file"
        ref={inputRef}
        accept={accept}
        style={{ display: 'none' }}
        onChange={onChange}
      />
    </div>
  );

  return (
    <>
      <div className={cx('bulk_request_header')}>
        <h2 className={cx('title')}>{STUDENT_BULK_ADD_REQUEST}</h2>
        <div className={cx('desc_area')}>
          <p className={cx('desc')}>{STUDENT_BULK_ADD_REQUEST_DESC}</p>
        </div>
      </div>

      <div className={cx('bulk_request_body')}>
        <div className={cx('bulk_request_body_content')}>
          <div className={cx('request_type_section')}>
            <div className={cx('section_title')}>{REQUEST_TYPE}</div>
            <div className={cx('radio_group')}>
              <span className={cx('radio_wrap')}>
                <label>
                  <input
                    type="radio"
                    name="request_type"
                    className="blind"
                    checked={requestType === 'student'}
                    onChange={() => setRequestType('student')}
                  />
                  <span className={cx('rlabel')}>
                    {STUDENT_BULK_ADD_ACTION}
                  </span>
                </label>
              </span>
              <span className={cx('radio_wrap')}>
                <label>
                  <input
                    type="radio"
                    name="request_type"
                    className="blind"
                    checked={requestType === 'group'}
                    onChange={() => setRequestType('group')}
                  />
                  <span className={cx('rlabel')}>{GROUP_BULK_ADD_ACTION}</span>
                </label>
              </span>
              <span className={cx('radio_wrap')}>
                <label>
                  <input
                    type="radio"
                    name="request_type"
                    className="blind"
                    checked={requestType === 'etc'}
                    onChange={() => setRequestType('etc')}
                  />
                  <span className={cx('rlabel')}>{ETC}</span>
                </label>
              </span>
            </div>
          </div>

          {requestType !== 'etc' && (
            <div className={cx('download_section')}>
              <div className={cx('section_title')}>{FORM_DOWNLOAD}</div>
              <div className={cx('download_links')}>
                {requestType === 'student' ? (
                  <>
                    <a
                      href="#"
                      className={cx('download_link')}
                      onClick={(e) => handleDownload(e, 'excel')}
                    >
                      <i className={cx('download_icon')}></i>
                      {STUDENT_INFO_EXCEL_FORM}
                    </a>
                    <a
                      href="#"
                      className={cx('download_link')}
                      onClick={(e) => handleDownload(e, 'consent')}
                    >
                      <i className={cx('download_icon')}></i>
                      {PRIVACY_CONSENT_FORM}
                    </a>
                  </>
                ) : requestType === 'group' ? (
                  <>
                    <a
                      href="#"
                      className={cx('download_link', {
                        'is--disabled': isNoStudent,
                      })}
                      onClick={(e) => handleDownload(e, 'group')}
                    >
                      <i className={cx('download_icon')}></i>
                      {GROUP_INFO_EXCEL_FORM}
                    </a>
                  </>
                ) : null}
              </div>
              {isNoStudent && (
                <p className={cx('download_notice')}>
                  ※ {GROUP_BULK_ADD_NOTICE}
                </p>
              )}
            </div>
          )}

          <div className={cx('content_section')}>
            <div className={cx('content_form')}>
              <div className={cx('section_title')}>{REQUEST_CONTENT_WRITE}</div>
              <div className={cx('input_group')}>
                <div className={cx('input_field')}>
                  <input
                    type="text"
                    placeholder={ENTER_TITLE}
                    value={title}
                    onChange={handleTitleChange}
                    maxLength={25}
                  />
                </div>
                <div className={cx('input_field')}>
                  <textarea
                    className={cx('textarea_field')}
                    placeholder={ENTER_CONTENT}
                    value={content}
                    onChange={handleContentChange}
                    maxLength={1000}
                  ></textarea>
                </div>
              </div>
            </div>
            <div
              className={cx('char_count', { limit: content.length >= 1000 })}
            >
              {content.length.toLocaleString()}/1,000{CHARACTER_COUNT}
            </div>
          </div>

          <div className={cx('upload_section')}>
            {requestType === 'student' ? (
              <>
                <FileUploadField
                  file={excelFile}
                  placeholder={EXCEL_UPLOAD_PLACEHOLDER}
                  inputRef={excelInputRef}
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileChange}
                />
                <FileUploadField
                  file={consentFile}
                  placeholder={CONSENT_UPLOAD_PLACEHOLDER}
                  inputRef={consentInputRef}
                  accept=".docx,.pdf,.zip"
                  onChange={handleConsentFileChange}
                />
                <div className={cx('upload_notice')}>
                  ※ {parse(UPLOAD_NOTICE)}
                </div>
              </>
            ) : requestType === 'group' ? (
              <>
                <FileUploadField
                  file={excelFile}
                  placeholder={GROUP_UPLOAD_PLACEHOLDER}
                  inputRef={excelInputRef}
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileChange}
                />
              </>
            ) : requestType === 'etc' ? (
              <>
                <FileUploadField
                  file={excelFile}
                  placeholder={FILE_UPLOAD_PLACEHOLDER}
                  inputRef={excelInputRef}
                  accept="*"
                  onChange={handleExcelFileChange}
                />
              </>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className={cx('btn', 'b_t', 'request', {
            'is--disabled':
              !title.trim() ||
              !content.trim() ||
              !excelFile ||
              (requestType === 'student' && !consentFile) ||
              isNoStudent,
          })}
          onClick={handleRequestClick}
        >
          {REQUEST_ACTION}
        </button>
      </div>
    </>
  );
}
